// film.js: the one scroll axis. Five chapters sit end to end on a tall
// invisible track (lengths in "screens" = large-viewport heights), then the
// offer scrolls in as a normal page. Each boundary between two chapters is a
// transition window of 2 * half screens centred on the boundary: the
// outgoing world plays out its last beat while the incoming one holds its
// first frame, and a compositor shader cuts between them. Everything is a
// pure function of the scroll position, so scrolling backwards or jumping
// always looks right.
import { clamp } from './util.js';

export const CHAPTERS = [
  { key: 'ignite', name: 'Ignite', span: 2.7, tag: 'A protostar, rendered live' },
  { key: 'home', name: 'Home', span: 4.2, tag: 'Earth from orbit, rendered live', credit: 'Earth: NASA Blue Marble · City lights: NASA Black Marble 2016' },
  { key: 'scale', name: 'Scale', span: 3.6, tag: 'The Milky Way, rendered live', credit: 'Milky Way: ESO/S. Brunier, CC BY 4.0 (adapted)' },
  { key: 'gravity', name: 'Gravity', span: 4.2, tag: 'A black hole like Sgr A*, rendered live', credit: 'Milky Way: ESO/S. Brunier, CC BY 4.0 (adapted)' },
  { key: 'yours', name: 'Yours', span: 2.6, tag: 'A star generated from your name' },
  { key: 'offer', name: 'Work with us', span: 0, tag: 'hello@starlexmedia.com' },
];
export const FILM_CHAPTERS = 5; // chapters on the track (the offer is a page)

// PACE stretches the whole film: every chapter span AND every cut half-width
// are multiplied by it, so a chapter's own progress 0..1 (and every beat,
// label and cut window keyed to it) stays exactly where it was. Only the
// scroll distance per screen of story grows. v5.7 = 1 (the old length).
// URL override for A/B: ?pace=1.
export let PACE = 1;
export function setPace(k) { PACE = Math.max(0.5, Math.min(4, +k || 1)); }
const span = (i) => CHAPTERS[i].span * PACE;

export class Film {
  // half: transition half-width in screens, one number or one per cut
  constructor({ half = 0.5 } = {}) {
    this.halves = (Array.isArray(half) ? half.slice() : [half, half, half, half]).map((h) => h * PACE);
    this.half = Math.max(...this.halves);
    this.vh = 800;
    this.offerH = 800;
    this.starts = [];
    this.layout(800, 800);
  }

  layout(vh, offerH) {
    this.vh = Math.max(200, vh);
    this.offerH = Math.max(200, offerH);
    let s = 0;
    this.starts = [];
    for (let i = 0; i < FILM_CHAPTERS; i++) { this.starts.push(s); s += span(i); }
    this.total = s; // screens of film before the offer
    // the track holds the film plus one screen, so at its end the offer's
    // top edge sits exactly at the bottom of the viewport
    this.trackPx = Math.round((s + 1) * this.vh);
  }

  progressOf(i, u) {
    if (i >= FILM_CHAPTERS) return 1;
    return clamp((u - this.starts[i]) / span(i), 0, 1);
  }

  at(y, out = {}) {
    const u = Math.max(0, y / this.vh);
    let ch = 0;
    for (let i = 0; i < FILM_CHAPTERS; i++) if (u >= this.starts[i]) ch = i;
    let cut = -1, t = 0;
    for (let i = 0; i < FILM_CHAPTERS - 1; i++) {
      const b = this.starts[i + 1], h = this.halves[i];
      if (u > b - h && u < b + h) { cut = i; t = (u - (b - h)) / (2 * h); break; }
    }
    const past = u - this.total;
    out.u = u;
    out.filmCh = ch;
    out.p = this.progressOf(ch, u);
    out.cut = cut;
    out.t = t;
    out.offer = clamp(past, 0, 1); // how much of the viewport the offer covers
    out.p5 = clamp(((past - 1) * this.vh) / Math.max(1, this.offerH - this.vh), 0, 1);
    out.ch = out.offer > 0.5 ? 5 : ch; // chapter shown in the HUD
    out.global = clamp(u / (this.total + 1), 0, 1);
    return out;
  }

  yFor(ch, p = 0) {
    if (ch >= FILM_CHAPTERS) return Math.round((this.total + 1) * this.vh + p * Math.max(0, this.offerH - this.vh));
    return Math.round((this.starts[ch] + p * span(ch)) * this.vh);
  }

  yForCut(i, t = 0.5) {
    const b = this.starts[i + 1], h = this.halves[i];
    return Math.round((b - h + 2 * h * t) * this.vh);
  }

  // chapter progress where cut i starts (outgoing) / ends (incoming)
  cutSpan(i) {
    const h = this.halves[i];
    return { outP: 1 - h / span(i), inP: h / span(i + 1) };
  }
}
